/**
 * Marketing-attribution capture.
 *
 * On every page load we sniff the URL for the standard UTM bundle plus
 * Meta-platform click IDs (`fbclid`, `fbp`) so downstream analytics can
 * tell us which ad creative drove a session — even days later, after
 * the user signs in or upgrades to Premium.
 *
 * Two windows are persisted:
 *
 *   - **First touch** → `localStorage`. Sticky for the lifetime of the
 *     browser profile. Answers "which ad ultimately got this customer?"
 *     for cohort and LTV analysis.
 *   - **Last touch** → `sessionStorage`. Refreshed on every UTM hit.
 *     Answers "what was on-screen the moment they converted?".
 *
 * The bundle is also stamped onto a single `campaign_landed` event
 * (fired exactly once per session that carries UTM params) so it shows
 * up in GA4 and as a Meta Pixel CustomEvent — see {@link initAttribution}.
 *
 * Why a separate module: keeps `telemetry.ts` vendor-shaped (Meta /
 * Firebase). This one is shape-shaped (UTMs / click ids) and is the
 * single place that touches storage for marketing data.
 */

import { addBreadcrumb, track } from "./telemetry";
import { setMetaPixelUtmAdvancedMatching } from "./metaPixel";

const FIRST_TOUCH_KEY = "room-canvas:attribution:first";
const LAST_TOUCH_KEY = "room-canvas:attribution:last";

/**
 * The canonical UTM bundle. Mirrors GA4's auto-collected `traffic_source`
 * dimensions plus Meta's click-id parameters.
 *
 * - `utm_source` / `utm_medium` / `utm_campaign` are required by GA4 for
 *   correct channel grouping; we treat them as mandatory in our UTM
 *   scheme even though Meta's auto-fill doesn't always populate them.
 * - `fbclid` is the per-click identifier Meta adds to the destination
 *   URL. Required as the `fbc` parameter of any Conversions API event
 *   that's meant to deduplicate against a Pixel `PageView`.
 */
export interface AttributionBundle {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  /** Wall-clock ms when this touch was captured. */
  at: number;
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

function readBundleFromUrl(): AttributionBundle | null {
  if (typeof window === "undefined") return null;
  const sp = new URLSearchParams(window.location.search);
  const bundle: AttributionBundle = { at: Date.now() };
  let any = false;
  for (const key of UTM_KEYS) {
    const v = sp.get(key);
    if (v && v.length > 0) {
      bundle[key] = v.slice(0, 200);
      any = true;
    }
  }
  const fbclid = sp.get("fbclid");
  if (fbclid && fbclid.length > 0) {
    bundle.fbclid = fbclid.slice(0, 200);
    any = true;
  }
  return any ? bundle : null;
}

function safeRead(storage: Storage, key: string): AttributionBundle | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as AttributionBundle;
  } catch {
    return null;
  }
}

function safeWrite(storage: Storage, key: string, value: AttributionBundle): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota / private-mode — fine to drop. Analytics still fires the
    // session-scoped `campaign_landed` event.
  }
}

/** Read the persisted first-touch attribution, if any. */
export function getFirstTouch(): AttributionBundle | null {
  if (typeof window === "undefined") return null;
  return safeRead(window.localStorage, FIRST_TOUCH_KEY);
}

/** Read the in-session last-touch attribution, if any. */
export function getLastTouch(): AttributionBundle | null {
  if (typeof window === "undefined") return null;
  return safeRead(window.sessionStorage, LAST_TOUCH_KEY);
}

/**
 * Capture the URL's UTM bundle (if any), persist first + last touch,
 * fire the one-shot `campaign_landed` event, and forward to Meta Pixel
 * advanced matching so subsequent Pixel events carry the same context.
 *
 * Idempotent within a tab session: the `campaign_landed` event fires
 * AT MOST once per session even if `initAttribution` is called twice
 * (StrictMode, HMR). Subsequent UTM hits on the same session still
 * update last-touch storage.
 *
 * Call once from `main.tsx`, after `initTelemetry()` so analytics
 * forwards work.
 */
let sessionEventFired = false;
export function initAttribution(): void {
  if (typeof window === "undefined") return;
  const bundle = readBundleFromUrl();
  if (!bundle) return;

  // First-touch is sticky — only set on the very first capture.
  const existing = getFirstTouch();
  if (!existing) {
    safeWrite(window.localStorage, FIRST_TOUCH_KEY, bundle);
  }

  // Last-touch refreshes on every UTM hit within the session.
  safeWrite(window.sessionStorage, LAST_TOUCH_KEY, bundle);

  if (sessionEventFired) return;
  sessionEventFired = true;

  addBreadcrumb("attribution:captured", {
    source: bundle.utm_source,
    campaign: bundle.utm_campaign,
    has_fbclid: Boolean(bundle.fbclid),
  });

  // Stamp the click-id into Pixel advanced matching before the
  // CampaignLanded event so that hit (and later ViewContent / CompleteRegistration)
  // share the same attribution. The event itself is fired by
  // `track()` via META_EVENT_MAP — do not call metaPixelTrack again.
  setMetaPixelUtmAdvancedMatching(bundle);

  track("campaign_landed", {
    utm_source: bundle.utm_source ?? null,
    utm_medium: bundle.utm_medium ?? null,
    utm_campaign: bundle.utm_campaign ?? null,
    utm_content: bundle.utm_content ?? null,
    utm_term: bundle.utm_term ?? null,
  });
}

/** Test-only: wipe the in-memory dedup flag. */
export function _resetAttributionForTests(): void {
  sessionEventFired = false;
}
